import {
  TextFields as CaseIcon,
  CheckCircle as CheckIcon,
  Cancel as CrossIcon,
  ExpandMore as ExpandMoreIcon,
  Business as ProviderIcon,
  Gavel as RfcIcon,
  Rule as RuleIcon,
  PlayArrow as TryIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { ProviderInfo } from "../api/types";
import { useProviders } from "../hooks";

// Example emails for quick testing - grouped by category
const EXAMPLE_EMAILS = [
  {
    category: "Valid Formats",
    examples: [
      { email: "monica@gmail.com", description: "Simple Gmail address" },
      {
        email: "anjali.lastname@outlook.com",
        description: "Dotted local part",
      },
      {
        email: "sharon+newsletter@gmail.com",
        description: "Gmail with subaddress tag",
      },
      { email: "billy@company.co.ug", description: "Multi-part TLD" },
    ],
  },
  {
    category: "Internationalized Domains (IDN)",
    examples: [
      {
        email: "münchen.café@рф.中国",
        description: "Multi-script IDN (German, French, Russian, Chinese)",
      },
      { email: "will@münchen.de", description: "German umlaut in domain" },
      { email: "kev@example.рф", description: "Russian .рф TLD" },
      { email: "dan@example.中国", description: "Chinese .中国 TLD" },
      { email: "tim@café.com", description: "French accent in domain" },
    ],
  },
  {
    category: "Provider-Specific",
    examples: [
      {
        email: "JOHN.DOE@gmail.com",
        description: "Gmail ignores dots and case",
      },
      { email: "user-name@yahoo.com", description: "Yahoo allows hyphens" },
      {
        email: "user_name@hotmail.com",
        description: "Hotmail allows underscores",
      },
      { email: "contact@icloud.com", description: "iCloud/Apple Mail" },
    ],
  },
  {
    category: "Reserved & Special Domains",
    examples: [
      { email: "test@example.com", description: "RFC 2606 reserved domain" },
      { email: "user@mail.test", description: "RFC 6761 .test TLD" },
      { email: "user@localhost", description: "RFC 6761 localhost" },
      { email: "admin@example.org", description: "RFC 2606 example.org" },
    ],
  },
  {
    category: "Invalid Formats",
    examples: [
      { email: "user@", description: "Missing domain" },
      { email: "@domain.com", description: "Missing local part" },
      { email: "user@domain", description: "Missing TLD" },
      { email: "user@.com", description: "Empty domain label" },
      {
        email: "user..name@gmail.com",
        description: "Consecutive dots (Gmail rejects)",
      },
      { email: "user@domain.invalidtld", description: "Unregistered TLD" },
    ],
  },
];

export function LearnPage() {
  const { data: providers, isLoading, isError } = useProviders();

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom>
        Learn About Email Validation
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Understand how the Pragmatic Email Validator works and the rules it
        applies to validate email addresses.
      </Typography>

      {/* Try Examples Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TryIcon color="primary" />
            <Typography variant="h6">Try Examples</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            Click any example to validate it instantly. Opens in a new tab.
          </Typography>

          {EXAMPLE_EMAILS.map((group) => (
            <Box key={group.category} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {group.category}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {group.examples.map((example) => (
                  <Chip
                    key={example.email}
                    label={example.email}
                    component="a"
                    href={`/?email=${encodeURIComponent(example.email)}`}
                    target="_blank"
                    rel="noopener"
                    clickable
                    variant="outlined"
                    size="small"
                    title={example.description}
                    sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* Glossary Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CaseIcon color="primary" />
            <Typography variant="h6">Glossary of Terms</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            Key terminology used throughout email validation:
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "25%" }}>
                    Term
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Definition</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "25%" }}>
                    Example
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>local part</code>
                  </TableCell>
                  <TableCell>
                    The portion of the email address before the @ symbol. This
                    is typically the username.
                  </TableCell>
                  <TableCell>
                    <code>
                      <strong>john.smith</strong>@gmail.com
                    </code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>domain</code>
                  </TableCell>
                  <TableCell>
                    The portion of the email address after the @ symbol.
                    Identifies the mail server.
                  </TableCell>
                  <TableCell>
                    <code>
                      john.smith@<strong>gmail.com</strong>
                    </code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>TLD</code>
                  </TableCell>
                  <TableCell>
                    Top-Level Domain — the last segment of the domain (e.g.,
                    .com, .org, .uk). Must be registered with IANA.
                  </TableCell>
                  <TableCell>
                    <code>
                      john.smith@gmail.<strong>com</strong>
                    </code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>subaddress delimiter</code>
                  </TableCell>
                  <TableCell>
                    A special character (usually <code>+</code> or{" "}
                    <code>-</code>) that separates the base username from a tag.
                    Everything after this delimiter routes to the same mailbox
                    but can be used for filtering.
                  </TableCell>
                  <TableCell>
                    <code>
                      john.smith<strong>+</strong>promo@gmail.com
                    </code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>subaddress tag</code>
                  </TableCell>
                  <TableCell>
                    The text after the subaddress delimiter. Ignored for routing
                    but can be used to filter or categorize incoming mail.
                  </TableCell>
                  <TableCell>
                    <code>
                      john.smith+<strong>promo</strong>@gmail.com
                    </code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>base username</code>
                  </TableCell>
                  <TableCell>
                    The local part without any subaddress tag — the actual
                    account name.
                  </TableCell>
                  <TableCell>
                    <code>
                      <strong>john.smith</strong>+promo@gmail.com
                    </code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>canonical address</code>
                  </TableCell>
                  <TableCell>
                    The normalized form of an email that shows where it actually
                    routes. For Gmail, this removes dots and subaddress tags.
                  </TableCell>
                  <TableCell>
                    <code>john.smith+promo@gmail.com</code> →{" "}
                    <code>johnsmith@gmail.com</code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>MX record</code>
                  </TableCell>
                  <TableCell>
                    Mail eXchange DNS record — specifies which mail server
                    handles email for a domain.
                  </TableCell>
                  <TableCell>
                    <code>gmail-smtp-in.l.google.com</code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>punycode</code>
                  </TableCell>
                  <TableCell>
                    An ASCII-compatible encoding for internationalized domain
                    names, defined in{" "}
                    <Link
                      href="https://datatracker.ietf.org/doc/html/rfc3492"
                      target="_blank"
                      rel="noopener"
                    >
                      RFC 3492
                    </Link>
                    . Unicode domains are converted to <code>xn--</code>{" "}
                    prefixed ASCII for DNS lookup.
                  </TableCell>
                  <TableCell>
                    <code>münchen.de</code> → <code>xn--mnchen-3ya.de</code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>provider</code>
                  </TableCell>
                  <TableCell>
                    The email service that handles mail for a domain (detected
                    via MX records or domain matching).
                  </TableCell>
                  <TableCell>
                    Gmail, Outlook, Yahoo, Microsoft 365, Google Workspace
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Universal Rules Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RfcIcon color="primary" />
            <Typography variant="h6">
              Universal Email Rules (RFC 5321/5322)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            All email addresses are validated against the core RFC
            specifications that define email format standards. These rules apply
            universally before any provider-specific validation.
          </Typography>

          <List dense>
            <ListItem>
              <ListItemIcon>
                <RuleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Maximum Total Length: 254 characters"
                secondary={
                  <>
                    Per{" "}
                    <Link
                      href="https://datatracker.ietf.org/doc/html/rfc5321#section-4.5.3.1.3"
                      target="_blank"
                      rel="noopener"
                    >
                      RFC 5321 §4.5.3.1.3
                    </Link>
                    , the maximum path length is 256 octets, leaving 254 for the
                    address itself.
                  </>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <RuleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Maximum Local Part Length: 64 characters"
                secondary={
                  <>
                    Per{" "}
                    <Link
                      href="https://datatracker.ietf.org/doc/html/rfc5321#section-4.5.3.1.1"
                      target="_blank"
                      rel="noopener"
                    >
                      RFC 5321 §4.5.3.1.1
                    </Link>
                    , the local-part must not exceed 64 octets.
                  </>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <RuleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Required @ Symbol"
                secondary="Email must contain exactly one @ symbol separating local-part and domain."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <RuleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Domain Validation"
                secondary="The domain portion must be a valid hostname with proper DNS configuration."
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> While RFC 5322 defines the email message
              format and allows for more complex local-part syntax (like quoted
              strings), RFC 5321 defines SMTP transport limits. The Pragmatic
              Email Validator focuses on addresses that will actually work in
              practice, not just those that are technically valid per RFC 5322.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* TLD Validation Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RuleIcon color="primary" />
            <Typography variant="h6">TLD Validation (IANA Registry)</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            The top-level domain (TLD) of an email address must exist in the
            official IANA (Internet Assigned Numbers Authority) registry. This
            prevents validation of addresses with made-up or typo'd TLDs.
          </Typography>

          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Authoritative Source"
                secondary={
                  <>
                    TLDs are validated against the official IANA list at{" "}
                    <Link
                      href="https://data.iana.org/TLD/tlds-alpha-by-domain.txt"
                      target="_blank"
                      rel="noopener"
                    >
                      data.iana.org/TLD/tlds-alpha-by-domain.txt
                    </Link>
                  </>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Automatic Daily Updates"
                secondary="The API refreshes from IANA every 24 hours. A cached copy is bundled with the application as a fallback if IANA is unreachable."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="IDN TLD Support"
                secondary="Internationalized TLDs (like .рф, .中国, .भारत) are automatically converted to punycode and validated. See the IDN section below for details."
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Why this matters:</strong> IANA regularly approves new
              TLDs. By fetching the list dynamically, we ensure that recently
              approved TLDs like newer country codes or brand TLDs are
              recognized immediately, rather than waiting for a software update.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Reserved Domains & Addresses Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RfcIcon color="primary" />
            <Typography variant="h6">Reserved Domains & Addresses</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            Certain domain names and email addresses are reserved by internet
            standards or by email providers and cannot be used for normal email
            delivery.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            RFC 2606: Reserved Example Domains
          </Typography>
          <Typography variant="body2" paragraph>
            <Link
              href="https://datatracker.ietf.org/doc/html/rfc2606"
              target="_blank"
              rel="noopener"
            >
              RFC 2606
            </Link>{" "}
            reserves specific second-level domains for use in documentation and
            examples. These domains are guaranteed to never be privately
            registered:
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Domain</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Purpose</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>example.com</code>, <code>example.net</code>,{" "}
                    <code>example.org</code>
                  </TableCell>
                  <TableCell>
                    Reserved for documentation and illustrative examples
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>.example</code> (TLD)
                  </TableCell>
                  <TableCell>
                    Reserved top-level domain for examples in documentation
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
            RFC 6761: Special-Use Domain Names
          </Typography>
          <Typography variant="body2" paragraph>
            <Link
              href="https://datatracker.ietf.org/doc/html/rfc6761"
              target="_blank"
              rel="noopener"
            >
              RFC 6761
            </Link>{" "}
            defines additional special-use TLDs that have specific technical
            meanings and should never route real email:
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>TLD</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Example</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>.test</code>
                  </TableCell>
                  <TableCell>
                    Reserved for testing — DNS software should recognize and
                    handle specially
                  </TableCell>
                  <TableCell>
                    <code>user@myapp.test</code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>.invalid</code>
                  </TableCell>
                  <TableCell>
                    Guaranteed to be invalid — use when you need a clearly
                    non-working domain
                  </TableCell>
                  <TableCell>
                    <code>user@no-reply.invalid</code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>.localhost</code>
                  </TableCell>
                  <TableCell>
                    Reserved for loopback — always resolves to the local machine
                  </TableCell>
                  <TableCell>
                    <code>admin@mail.localhost</code>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Validation Note:</strong> When you validate an address
              like <code>user@anything.test</code>, the validator flags it as a
              reserved domain. These TLDs are useful for development and testing
              but cannot receive real email.
            </Typography>
          </Alert>

          <Typography variant="subtitle2" gutterBottom>
            Provider-Reserved Addresses
          </Typography>
          <Typography variant="body2" paragraph>
            Beyond RFC-reserved domains, email providers reserve certain
            local-part names that users cannot register. These restrictions
            serve administrative, security, or anti-abuse purposes:
          </Typography>

          <List dense>
            <ListItem>
              <ListItemIcon>
                <RfcIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary="RFC 5321 Required: postmaster@"
                secondary="Every domain MUST accept mail to postmaster@. Providers cannot allow users to register this name."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <RfcIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary="RFC 2142 Role Accounts"
                secondary={
                  <>
                    Names like <code>abuse@</code>, <code>webmaster@</code>,{" "}
                    <code>hostmaster@</code>, <code>admin@</code> are typically
                    reserved for operational roles.
                  </>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <ProviderIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="Provider-Specific Restrictions"
                secondary={
                  <>
                    Gmail blocks short usernames (under 6 characters), Yahoo
                    restricts numeric-only names, and most providers block
                    profanity and brand names.
                  </>
                }
              />
            </ListItem>
          </List>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" component="div">
              <strong>Custom Domain Considerations:</strong> If you use a custom
              domain with Microsoft 365 or Google Workspace, you control most
              addresses—but the provider may still restrict certain system
              accounts. For example:
              <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                <li>
                  Google Workspace reserves <code>postmaster@</code> and routes
                  it to super admins
                </li>
                <li>
                  Microsoft 365 reserves <code>abuse@</code> and{" "}
                  <code>postmaster@</code> by default
                </li>
              </ul>
              These restrictions protect domain owners from compliance issues.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Internationalized Domain Names Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RfcIcon color="primary" />
            <Typography variant="h6">
              Internationalized Domain Names (IDN)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            Internationalized Domain Names (IDN) allow domain names to use
            characters from non-Latin scripts like Cyrillic, Chinese, Arabic,
            and Hindi. The validator fully supports IDN domains per{" "}
            <Link
              href="https://datatracker.ietf.org/doc/html/rfc5891"
              target="_blank"
              rel="noopener"
            >
              RFC 5891 (IDNA 2008)
            </Link>
            .
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            How It Works: Punycode Conversion
          </Typography>
          <Typography variant="body2" paragraph>
            When you enter an email with a Unicode domain, the validator
            automatically converts it to ASCII-compatible encoding called
            "punycode" for validation and DNS lookup:
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    You Enter (Unicode)
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Validated As (Punycode)
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Country/Script
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>user@example.рф</code>
                  </TableCell>
                  <TableCell>
                    <code>user@example.xn--p1ai</code>
                  </TableCell>
                  <TableCell>Russia (.рф = .rf)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>user@example.中国</code>
                  </TableCell>
                  <TableCell>
                    <code>user@example.xn--fiqs8s</code>
                  </TableCell>
                  <TableCell>China (中国 = China)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>user@example.भारत</code>
                  </TableCell>
                  <TableCell>
                    <code>user@example.xn--h2brj9c</code>
                  </TableCell>
                  <TableCell>India (भारत = Bharat)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>user@münchen.de</code>
                  </TableCell>
                  <TableCell>
                    <code>user@xn--mnchen-3ya.de</code>
                  </TableCell>
                  <TableCell>German umlaut (ü)</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Try it:</strong> Enter an email like{" "}
              <code>test@example.рф</code> or <code>user@münchen.de</code> in
              the validator. It will correctly validate the domain by converting
              it to punycode internally.
            </Typography>
          </Alert>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note on "example" domains:</strong> Unlike the reserved{" "}
              <code>example.com</code> (RFC 2606), IDN equivalents like{" "}
              <code>example.中国</code> and <code>example.рф</code> are{" "}
              <em>real registered domains</em>. The RFC 2606 reservation only
              applies to ASCII <code>.com</code>, <code>.net</code>,{" "}
              <code>.org</code>, and <code>.edu</code> TLDs.
            </Typography>
          </Alert>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Technical Details
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Automatic Conversion"
                secondary="Unicode domains are converted to punycode (xn-- prefix) before TLD validation and DNS lookups."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="IANA TLD Support"
                secondary="Over 100 internationalized TLDs are registered with IANA and supported by the validator."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Mixed Scripts"
                secondary="Domains can mix ASCII and Unicode labels (e.g., example.中国 has ASCII 'example' and Chinese '中国')."
              />
            </ListItem>
          </List>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Provider Support Varies:</strong> While the domain may be
              technically valid, not all email providers accept
              internationalized local parts (the part before @). Most providers
              only support ASCII characters in the username portion.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Case Sensitivity Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CaseIcon color="primary" />
            <Typography variant="h6">Case Sensitivity</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            Email address case sensitivity is more nuanced than many realize:
          </Typography>

          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Domain Part: Always Case-Insensitive"
                secondary="Per RFC 5321, the domain portion of an email is case-insensitive. 'gmail.com' and 'GMAIL.COM' are equivalent."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CrossIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary="Local Part: Technically Case-Sensitive"
                secondary="Per RFC 5321, the local-part is technically case-sensitive. 'User' and 'user' could theoretically be different mailboxes."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="In Practice: Most Providers Are Case-Insensitive"
                secondary="Major providers like Gmail, Outlook, and Yahoo treat the local-part as case-insensitive for user convenience."
              />
            </ListItem>
          </List>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Best Practice:</strong> Always store and compare email
              addresses in lowercase, but preserve the original case for display
              purposes. The normalized email returned by the API reflects this
              approach.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Provider-Specific Rules Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ProviderIcon color="primary" />
            <Typography variant="h6">Provider-Specific Rules</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            Different email providers enforce their own rules beyond the RFC
            standards. The validator recognizes these providers and applies
            their specific constraints. PEV now models providers by two core
            capabilities: domain namespace scope (fixed domains only vs custom
            domain support) and address scope (mailbox-hosting vs delivery
            infrastructure).
          </Typography>

          {isLoading && (
            <Box>
              <Skeleton variant="rectangular" height={200} />
            </Box>
          )}

          {isError && (
            <Alert severity="error">
              Unable to load provider information. Please try again later.
            </Alert>
          )}

          {providers && providers.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell>Domain Scope</TableCell>
                    <TableCell>Address Scope</TableCell>
                    <TableCell>Domain Signals</TableCell>
                    <TableCell align="center">Max Local</TableCell>
                    <TableCell align="center">Sub-addressing</TableCell>
                    <TableCell align="center">Internationalized</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {providers.map((provider: ProviderInfo) => (
                    <TableRow key={provider.providerId}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {provider.displayName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {provider.providerId}
                        </Typography>
                        {provider.documentation && provider.documentation.length > 0 && (
                          <Box sx={{ mt: 0.5, display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                            {provider.documentation.slice(0, 2).map((doc) => (
                              <Link
                                key={doc.url}
                                href={doc.url}
                                target="_blank"
                                rel="noopener"
                                variant="caption"
                                underline="hover"
                              >
                                {doc.description}
                              </Link>
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={provider.domainScope === "SupportsCustomDomains" ? "primary" : "default"}
                          label={
                            provider.domainScope === "SupportsCustomDomains"
                              ? "Custom + fixed (if any)"
                              : "Fixed domains only"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={provider.addressScope === "MailboxHosting" ? "success" : "warning"}
                          label={
                            provider.addressScope === "MailboxHosting"
                              ? "Mailbox hosting"
                              : "Delivery infrastructure"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {provider.fixedDomains?.slice(0, 2).map((domain) => (
                            <Chip key={domain} label={domain} size="small" variant="outlined" />
                          ))}
                          {provider.fixedDomains && provider.fixedDomains.length > 2 && (
                            <Chip
                              label={`+${provider.fixedDomains.length - 2} fixed`}
                              size="small"
                              color="default"
                            />
                          )}
                          {!provider.fixedDomains?.length && provider.mxMatchPattern && (
                            <Chip
                              label="MX pattern"
                              size="small"
                              color="default"
                              title={provider.mxMatchPattern}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {provider.rules.maxLocalPartLength}
                      </TableCell>
                      <TableCell align="center">
                        {provider.rules.subaddressingMode !== "NotSupported" ? (
                          <CheckIcon color="success" fontSize="small" />
                        ) : (
                          <CrossIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {provider.rules.allowInternationalized ? (
                          <CheckIcon color="success" fontSize="small" />
                        ) : (
                          <CrossIcon color="disabled" fontSize="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Legend:
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CheckIcon color="success" fontSize="small" />
                <Typography variant="caption">Supported</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CrossIcon color="error" fontSize="small" />
                <Typography variant="caption">Not Supported</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CrossIcon color="disabled" fontSize="small" />
                <Typography variant="caption">Not Applicable</Typography>
              </Box>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Model note:</strong> Delivery-infrastructure providers
              (identity, relay, and gateway systems) can appear in detection,
              but they are treated as not applicable for mailbox username
              policy validation.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Subaddressing Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RuleIcon color="primary" />
            <Typography variant="h6">
              Subaddressing (Plus Addressing)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            Subaddressing (also called "plus addressing" or "tagged addresses")
            allows users to create variations of their email address that all
            route to the same inbox. For example,{" "}
            <code>user+newsletter@gmail.com</code> routes to{" "}
            <code>user@gmail.com</code>.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Key Points:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Valid for Receiving Mail"
                secondary="Subaddressed emails are valid addresses that will successfully receive mail at the base address."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CrossIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Cannot Be Created as Accounts"
                secondary="You cannot register 'user+tag@gmail.com' as a Gmail account - only the base address 'user' can be registered."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="Provider-Specific Delimiters"
                secondary="Gmail and many others use '+'. Microsoft 365 can be tenant-configurable. Yahoo dash aliases require setup and are treated as out-of-scope for strict mailbox account validation."
              />
            </ListItem>
          </List>

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Delimiter</TableCell>
                  <TableCell>Example</TableCell>
                  <TableCell>Routes To</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Gmail</TableCell>
                  <TableCell>
                    <code>+</code>
                  </TableCell>
                  <TableCell>
                    <code>user+promo@gmail.com</code>
                  </TableCell>
                  <TableCell>
                    <code>user@gmail.com</code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Outlook</TableCell>
                  <TableCell>
                    <code>+</code>
                  </TableCell>
                  <TableCell>
                    <code>user+work@outlook.com</code>
                  </TableCell>
                  <TableCell>
                    <code>user@outlook.com</code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Yahoo</TableCell>
                  <TableCell>
                    <code>(setup)</code>
                  </TableCell>
                  <TableCell>
                    <em>Dash aliases are optional/disposable-address features</em>
                  </TableCell>
                  <TableCell><em>Treated as not universally supported</em></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>AOL</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell colSpan={2}>
                    <em>Not supported</em>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Validation Behavior:</strong> When PEV detects a
              subaddressed email, it validates the <em>base</em> local part
              against the provider's rules (length, character patterns, etc.)
              and reports the subaddress information separately. The email is
              valid if the base address would be valid.
            </Typography>
          </Alert>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" component="div">
              <strong>Gmail Length Calculation:</strong> Gmail requires a
              minimum of 6 characters in the username. When calculating length,
              PEV excludes:
              <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                <li>
                  <strong>Dots (.)</strong> — Gmail treats dots as "not
                  significant"
                </li>
                <li>
                  <strong>Subaddress tag (+tag)</strong> — The +tag portion
                  routes mail but isn't part of the account name
                </li>
              </ul>
              <strong>Example:</strong> <code>bill+promo@gmail.com</code> → base
              is <code>bill</code> → 4 chars &lt; 6 = <strong>Invalid</strong>
              <br />
              <strong>Example:</strong> <code>b.i.l.l+tag@gmail.com</code> →
              base is <code>b.i.l.l</code> → without dots = 4 chars &lt; 6 ={" "}
              <strong>Invalid</strong>
            </Typography>
          </Alert>

          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2" component="div">
              <strong>Canonical Address ("Routes To"):</strong> PEV shows the
              normalized mailbox address that an email will actually route to.
              This is helpful because:
              <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                <li>
                  <code>bill.wilder@gmail.com</code> routes to{" "}
                  <code>billwilder@gmail.com</code> (dots ignored)
                </li>
                <li>
                  <code>john.smith+promo@gmail.com</code> routes to{" "}
                  <code>johnsmith@gmail.com</code> (dots + tag ignored)
                </li>
              </ul>
              This helps you understand that these variations all deliver to the
              same inbox, which is important for duplicate detection and user
              management.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* API Reference Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RuleIcon color="primary" />
            <Typography variant="h6">API Reference</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            The validation API provides endpoints for email validation, provider
            information, and service health monitoring:
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" component="div">
              <strong>API Endpoints:</strong>
              <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                <li>
                  <strong>Base URL:</strong>{" "}
                  <Link
                    href="https://api.pragmaticemailvalidator.com"
                    target="_blank"
                    rel="noopener"
                  >
                    <code>https://api.pragmaticemailvalidator.com</code>
                  </Link>
                </li>
                <li>
                  <strong>Validate:</strong>{" "}
                  <code>GET /api/v1/validate?email=user@example.com</code>
                </li>
                <li>
                  <strong>Providers:</strong> <code>GET /api/v1/providers</code>
                </li>
                <li>
                  <strong>Liveness:</strong>{" "}
                  <Link
                    href="https://api.pragmaticemailvalidator.com/livez"
                    target="_blank"
                    rel="noopener"
                  >
                    <code>/livez</code>
                  </Link>{" "}
                  — returns plain text <code>ok</code> (used by{" "}
                  <Link
                    href="https://learn.microsoft.com/en-us/azure/container-apps/health-probes"
                    target="_blank"
                    rel="noopener"
                  >
                    ACA health probes
                  </Link>
                  )
                </li>
                <li>
                  <strong>Readiness:</strong>{" "}
                  <Link
                    href="https://api.pragmaticemailvalidator.com/readyz"
                    target="_blank"
                    rel="noopener"
                  >
                    <code>/readyz</code>
                  </Link>{" "}
                  (version, TLD cache status)
                </li>
                <li>
                  <strong>OpenAPI Docs:</strong>{" "}
                  <Link
                    href="https://api.pragmaticemailvalidator.com/scalar/v1"
                    target="_blank"
                    rel="noopener"
                  >
                    <code>/scalar/v1</code>
                  </Link>{" "}
                  (interactive API documentation)
                </li>
              </ul>
            </Typography>
          </Alert>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Field</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>email</code>
                  </TableCell>
                  <TableCell>The email address that was validated</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>isValid</code>
                  </TableCell>
                  <TableCell>Overall validation result (true/false)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>passedUniversalRules</code>
                  </TableCell>
                  <TableCell>
                    Whether basic RFC-compliant syntax validation passed
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>detectedProvider</code>
                  </TableCell>
                  <TableCell>
                    Detected email provider with ID, display name, and category
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>universalFailures</code>
                  </TableCell>
                  <TableCell>
                    List of failures from universal/RFC validation rules
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>providerFailures</code>
                  </TableCell>
                  <TableCell>
                    List of failures from provider-specific validation rules
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>dnsCheck</code>
                  </TableCell>
                  <TableCell>
                    DNS verification results (domain exists, MX records, MX
                    hosts)
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>subaddress</code>
                  </TableCell>
                  <TableCell>
                    Subaddressing info when detected (baseLocalPart,
                    subaddressTag, routesTo)
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>canonicalAddress</code>
                  </TableCell>
                  <TableCell>
                    The normalized address this email routes to (dots removed,
                    subaddress stripped). Only present when different from the
                    original address.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
